import { Strategy, TicketType } from "@faire/help-center-self-serve/consts";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { browserLevelLocaleKey } from "@faire/help-center-utils/serialized-data/getLocale";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { getRetailerOrBrandCountry } from "@faire/help-center-utils/serialized-data/getRetailerOrBrandCountry";
import { getUserToken } from "@faire/help-center-utils/serialized-data/getUser";
import { getSettingChatMessengerWindowLogoUrl } from "@faire/help-center-utils/settings/getSettingChatMessengerWindowLogoUrl";
import { getSettingChatMessengerWindowTitle } from "@faire/help-center-utils/settings/getSettingChatMessengerWindowTitle";
import { getSettingHelpCenterTicketPeteEventsFromBE } from "@faire/help-center-utils/settings/getSettingHelpCenterTicketPeteEventsFromBE";
import { getSettingSunCoIntegrationId } from "@faire/help-center-utils/settings/getSettingSunCoIntegrationId";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { buildLocale } from "@faire/web--source/common/localization/utils";
import { logWarning } from "@faire/web--source/common/logging";
import { Storage } from "@faire/web--source/common/Storage";
import fetchJwtAuth from "@faire/web-api--source/api/help-center/messenger/auth/get";
import fetchConversation from "@faire/web-api--source/api/help-center/messenger/conversation/get";
import { trackHelpCenterAllTypesOfTicketCreationSubmit } from "@faire/web-api--source/events/helpCenter/submit/allTypesOfTicketCreation";
import { trackHelpCenterChatCsatSubmit } from "@faire/web-api--source/events/helpCenter/submit/chatCsat";
import { trackHelpCenterChatCsatView } from "@faire/web-api--source/events/helpCenter/view/chatCsat";
import { IHCMessengerAuthResponse } from "@faire/web-api--source/indigofair/helpcenter/IHCMessengerAuthResponse";
import { Language } from "@faire/web-api--source/indigofair/languages/Language";
import { IFaireMetadata } from "@faire/web-api--source/sunco/IFaireMetadata";
import Smooch from "smooch";

import {
  addHotjarToIframeAttribute,
  displayTopBanner,
  hideInputField,
  hideTopBanner,
  removeSunCoLogo,
  showInputField,
} from "./chatCssOverrides";
import {
  ANSWER_BOT_NAME,
  FAIRE_BOT_NAME,
  FAIRE_BOT_ROLE,
  FAIRE_CHAT_CSAT_FEEDBACK_SUBMITTED_STORAGE_KEY,
  FAIRE_CHAT_METADATA,
  INIT_SEND_FAIRE_BOT_PROMPT_MESSAGE,
  MessagePayload,
} from "./consts";
import { customTexts } from "./customTexts";

const zdAgentInfo = {
  agentName: "",
  ticketId: "",
};

class ChatError extends Error {
  constructor(error: Error) {
    super();
    this.message = `Chat Error: ${error?.message}`;
    this.name = "ChatError";
    this.stack = error?.stack;
  }
}

const initChatWidget = (
  auth: IHCMessengerAuthResponse,
  strictLocalize: StrictLocalizeFunction,
  metaData?: IFaireMetadata
): Promise<void> => {
  if (!getSettingSunCoIntegrationId()) {
    const error = new Error(`Chat widget is not enabled for the user`);
    return Promise.reject(error);
  }

  // Initialize Smooch and set up the chat widget style to match Faire's style
  return Smooch.init({
    integrationId: getSettingSunCoIntegrationId(),
    jwt: auth.jwt,
    externalId: auth.external_id,
    locale: buildLocale(browserLevelLocaleKey()),
    embedded: false,
    displayStyle: "button",
    fixedHeader: true,
    businessIconUrl: getSettingChatMessengerWindowLogoUrl(),
    businessName: getSettingChatMessengerWindowTitle(),
    menuItems: {
      imageUpload: true,
      fileUpload: true,
      shareLocation: false,
    },
    canUserSeeConversationList: false,
    customColors: {
      conversationColor: "333333",
      brandColor: "FFFFFF",
      actionColor: "333333",
    },
    customText: { ...customTexts(strictLocalize) },
    buttonIconUrl:
      "https://cdn.faire.com/fastly/a2ede5d047279d3d307e2861c07ee6e429a5b28c411621f2d5fd648d735409e2.png",
    delegate: {
      beforeDisplay(message, _data) {
        const { text, displayName, payload } = message;
        if (
          displayName === ANSWER_BOT_NAME ||
          payload === MessagePayload.INIT_SEND_FAIRE_BOT_PROMPT
        ) {
          return null;
        }
        if (
          displayName === FAIRE_BOT_NAME &&
          payload === MessagePayload.CSAT_MESSAGE
        ) {
          zdAgentInfo.agentName = `${message.metadata?.ticketAssigneeFirstName} ${message.metadata?.ticketAssigneeLastName}`;
          zdAgentInfo.ticketId = `${message.metadata?.ticketId}`;
        }
        if (text && payload !== MessagePayload.INIT_MESSAGE) {
          hideTopBanner();
        }
        return message;
      },
      beforeSend(message, _data) {
        message.metadata = {
          ...(metaData as Metadata),
        };

        return message;
      },
      onInvalidAuth() {
        return fetchJwtAuth().then((auth) => {
          const { jwt } = auth;
          if (!jwt) {
            return Promise.reject(
              new ChatError(
                new Error("Chat widget failed to get JWT from Zendesk API.")
              )
            );
          }
          return new Promise((resolve) => resolve(jwt));
        });
      },
    },
  }).then(() => {
    displayTopBanner(strictLocalize);
    removeSunCoLogo();
    showInputField();
    addHotjarToIframeAttribute();
    const conversations = Smooch.getConversations();
    // If there are no conversations, create a new one and send the initial message
    if (conversations.length === 0) {
      Smooch.createConversation({
        metadata: {
          language: toConversationMetadataLanguage(metaData?.language),
        },
        messages: [
          {
            text: INIT_SEND_FAIRE_BOT_PROMPT_MESSAGE,
            type: "text",
            payload: MessagePayload.INIT_SEND_FAIRE_BOT_PROMPT,
            metadata: {
              ...metaData,
            },
          },
        ],
      });
    } else {
      // If there are conversations, check if the conversation has messages
      if (conversations[0]?.messages.length === 0) {
        const conversationId = conversations[0].id;
        // Update the conversation metadata with the new language selection before sending the init prompt message
        Smooch.updateConversation(conversationId, {
          metadata: {
            language: toConversationMetadataLanguage(metaData?.language),
          },
        }).then(() => {
          Smooch.sendMessage(
            {
              type: "text",
              text: INIT_SEND_FAIRE_BOT_PROMPT_MESSAGE,
              payload: MessagePayload.INIT_SEND_FAIRE_BOT_PROMPT,
              metadata: {
                ...metaData,
              },
            },
            conversationId
          );
        });
      }
    }
  });
};

export const initChatAndUpdateLocalStorage = async (
  auth: IHCMessengerAuthResponse,
  strictLocalize: StrictLocalizeFunction,
  metaData: IFaireMetadata
) => {
  try {
    if (metaData) {
      Storage.local.setItem(FAIRE_CHAT_METADATA, JSON.stringify(metaData));
    }
    await initChatWidget(auth, strictLocalize, metaData);
  } catch (error) {
    const errorToLog =
      (error as Error).name === "ChatError"
        ? error
        : new ChatError(error as Error);
    logWarning(errorToLog, {
      autoFingerprint: true,
    });
  }
};

export const showChatWidget = async (
  auth: IHCMessengerAuthResponse,
  strictLocalize: StrictLocalizeFunction,
  metaData: IFaireMetadata
) => {
  await initChatAndUpdateLocalStorage(auth, strictLocalize, metaData);
  Smooch.open();
};

const destroyChatWidget = () => {
  Smooch.logout();
  Smooch.destroy();
  Storage.local.removeItem(FAIRE_CHAT_CSAT_FEEDBACK_SUBMITTED_STORAGE_KEY);
  Storage.local.removeItem(FAIRE_CHAT_METADATA);
};

Smooch.on("message:received", (message, _data) => {
  const { role, displayName, payload } = message;
  if (role === FAIRE_BOT_ROLE && displayName === FAIRE_BOT_NAME) {
    if (payload === MessagePayload.END_TEXT_MESSAGE) {
      hideInputField();
      resetLocalStorageWhenChatEnds();
    } else if (payload === MessagePayload.CSAT_MESSAGE) {
      trackHelpCenterChatCsatView(
        zdAgentInfo.ticketId,
        "",
        getRetailerOrBrandCountry()
      );
      hideInputField();
      resetLocalStorageWhenChatEnds();
    } else if (payload === MessagePayload.OFFLINE_MESSAGE) {
      hideInputField();
      resetLocalStorageWhenChatEnds();
    } else if (payload === MessagePayload.TRANSFER_MESSAGE) {
      const metaData = Storage.local.getItem(FAIRE_CHAT_METADATA);
      const { brand_order_token, email, issue_type } = metaData
        ? (JSON.parse(metaData) as IFaireMetadata)
        : ({} as IFaireMetadata);

      if (!getSettingHelpCenterTicketPeteEventsFromBE()) {
        trackHelpCenterAllTypesOfTicketCreationSubmit(
          getRetailerToken() ?? "",
          getBrandToken() ?? "",
          brand_order_token ?? "",
          email ?? "",
          "",
          "",
          "",
          issue_type ?? "",
          TicketType.CHAT,
          zdAgentInfo.ticketId,
          email ?? "",
          Strategy.SELF_SERVE,
          getUserToken() ?? ""
        );
      }
    }
  }
});

Smooch.on("widget:closed", () => {
  fetchConversation().then((conversation) => {
    if (!conversation.is_conversation_started) {
      destroyChatWidget();
    }
  });
});

Smooch.on("message:sent", (message, _data) => {
  const { payload } = message;
  if (payload === MessagePayload.NPS_GOOD) {
    trackHelpCenterChatCsatSubmit(
      zdAgentInfo.ticketId,
      "Good",
      getRetailerOrBrandCountry()
    );
    resetLocalStorageWhenChatEnds();
  } else if (payload === MessagePayload.NPS_BAD) {
    trackHelpCenterChatCsatSubmit(
      zdAgentInfo.ticketId,
      "Bad",
      getRetailerOrBrandCountry()
    );
    resetLocalStorageWhenChatEnds();
  }
  return message;
});

const resetLocalStorageWhenChatEnds = () => {
  Storage.local.removeItem(FAIRE_CHAT_METADATA);
};

/**
 * Converts the given language from the `Language` enum string to the version that's expected in the Sunco
 * conversation metadata, which is the values for the "User Language Preference" ticket dropdown field in Zendesk.
 */
const toConversationMetadataLanguage = (
  language: keyof typeof Language | undefined
): string | number | boolean => {
  switch (language) {
    case "ENG":
      return "langselect_us";
    case "FRA":
      return "langselect_fr";
    case "DEU":
      return "langselect_de";
    default:
      return "";
  }
};
